import React, { useState, useEffect } from 'react';
import { Calculator, Search, Pin, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Factors = ({ onSubmit, initialData = {} }) => {
  const { t } = useTranslation();
  const defaultFormData = React.useMemo(() => ({
    gwpVersion: 'AR5',
    factorSources: {
      electricity: { source: '', version: '', pinned: false },
      gas: { source: '', version: '', pinned: false },
      fuel: { source: '', version: '', pinned: false },
      transport: { source: '', version: '', pinned: false },
      waste: { source: '', version: '', pinned: false },
      water: { source: '', version: '', pinned: false }
    },
    regionalization: {
      country: '',
      subRegion: ''
    },
    marketBased: {
      method: '',
      supplierSpecific: {
        enabled: false,
        factor: '',
        evidence: {
          contract: '',
          guarantees: '',
          ppa: ''
        }
      },
      residualMix: {
        enabled: false,
        factor: ''
      }
    },
    pinned: false
  }), []);

  const [formData, setFormData] = useState(defaultFormData);

  // Load initial data when component mounts or initialData changes
  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      setFormData(prev => ({
        ...defaultFormData,
        ...initialData
      }));
    }
  }, [initialData, defaultFormData]);

  // Auto-save to localStorage whenever formData changes
  useEffect(() => {
    if (formData && Object.keys(formData).length > 0) {
      try {
        const currentData = JSON.parse(localStorage.getItem('calculationWorkflowData') || '{}');
        const updatedData = {
          ...currentData,
          factors: formData
        };
        localStorage.setItem('calculationWorkflowData', JSON.stringify(updatedData));
      } catch (error) {
        console.error('Error saving Factors data to localStorage:', error);
      }
    }
  }, [formData]);

  const [errors, setErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  // Available factor sources
  const factorSources = [
    { id: 'defra', name: 'DEFRA', description: 'UK Department for Environment, Food & Rural Affairs' },
    { id: 'iea', name: 'IEA', description: 'International Energy Agency' },
    { id: 'glec', name: 'GLEC', description: 'Global Logistics Emissions Council' },
    { id: 'egrid', name: 'eGRID', description: 'EPA Emissions & Generation Resource Integrated Database' },
    { id: 'custom', name: 'Custom', description: 'Custom emission factors' }
  ];

  // GWP versions
  const gwpVersions = [
    { id: 'AR5', name: t('workflow.calculation.factors.gwpVersion.ar5'), default: true },
    { id: 'AR6', name: t('workflow.calculation.factors.gwpVersion.ar6'), default: false }
  ];

  // Countries for regionalization
  const countries = [
    { code: 'US', name: 'United States' },
    { code: 'CA', name: 'Canada' },
    { code: 'GB', name: 'United Kingdom' },
    { code: 'DE', name: 'Germany' },
    { code: 'FR', name: 'France' },
    { code: 'PL', name: 'Poland' },
    { code: 'NL', name: 'Netherlands' },
    { code: 'SE', name: 'Sweden' },
    { code: 'NO', name: 'Norway' },
    { code: 'DK', name: 'Denmark' }
  ];

  const handleInputChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: field ? {
        ...prev[section],
        [field]: value
      } : value
    }));
    // Clear error when user starts typing
    if (errors[`${section}_${field}`]) {
      setErrors(prev => ({
        ...prev,
        [`${section}_${field}`]: undefined
      }));
    }
  };

  const handleFactorSourceChange = (category, field, value) => {
    setFormData(prev => ({
      ...prev,
      factorSources: {
        ...prev.factorSources,
        [category]: {
          ...prev.factorSources[category],
          [field]: value
        }
      }
    }));
  };

  const handleMarketBasedChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      marketBased: {
        ...prev.marketBased,
        [field]: value
      }
    }));
  };

  const handleMarketBasedObjectChange = (objectName, updates) => {
    setFormData(prev => ({
      ...prev,
      marketBased: {
        ...prev.marketBased,
        [objectName]: {
          ...prev.marketBased[objectName],
          ...updates
        }
      }
    }));
  };

  const handleSupplierEvidenceChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      marketBased: {
        ...prev.marketBased,
        supplierSpecific: {
          ...prev.marketBased.supplierSpecific,
          evidence: {
            ...prev.marketBased.supplierSpecific.evidence,
            [field]: value
          }
        }
      }
    }));
  };

  const togglePin = (category) => {
    setFormData(prev => ({
      ...prev,
      factorSources: {
        ...prev.factorSources,
        [category]: {
          ...prev.factorSources[category],
          pinned: !prev.factorSources[category].pinned
        }
      }
    }));
  };

  const toggleGlobalPin = () => {
    setFormData(prev => ({
      ...prev,
      pinned: !prev.pinned
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate GWP version
    if (!formData.gwpVersion) {
      newErrors.gwpVersion = t('workflow.calculation.factors.gwpVersion.required');
    }

    // Validate factor sources for each category
    Object.keys(formData.factorSources).forEach(category => {
      const source = formData.factorSources[category];
      if (!source.source) {
        newErrors[`${category}_source`] = t('workflow.calculation.factors.factorSources.sourceRequired');
      }
      if (!source.version) {
        newErrors[`${category}_version`] = t('workflow.calculation.factors.factorSources.versionRequired');
      }
    });

    // Validate regionalization
    if (!formData.regionalization.country) {
      newErrors.country = t('workflow.calculation.factors.regionalization.countryRequired');
    }

    // Validate market-based method
    if (formData.marketBased.method === 'supplier') {
      if (!formData.marketBased.supplierSpecific.factor) {
        newErrors.supplierFactor = t('workflow.calculation.factors.marketBased.supplierEvidence.factorRequired');
      }
      if (!formData.marketBased.supplierSpecific.evidence.contract && 
          !formData.marketBased.supplierSpecific.evidence.guarantees && 
          !formData.marketBased.supplierSpecific.evidence.ppa) {
        newErrors.supplierEvidence = t('workflow.calculation.factors.marketBased.supplierEvidence.evidenceRequired');
      }
    } else if (formData.marketBased.method === 'residual') {
      if (!formData.marketBased.residualMix.factor) {
        newErrors.residualFactor = t('workflow.calculation.factors.marketBased.residualMixEvidence.factorRequired');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const filteredSources = factorSources.filter(source =>
    source.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    source.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="bg-white rounded-lg shadow-md p-8">
      <div className="flex items-center mb-6">
        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
          <Calculator className="w-6 h-6 text-blue-600" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-800">{t('workflow.calculation.factors.title')}</h2>
          <p className="text-gray-600">{t('workflow.calculation.factors.description')}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* GHG Protocol / GWP Selection */}
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            {t('workflow.calculation.factors.gwpVersion.title')}
          </h3>
          <div className="space-y-3">
            {gwpVersions.map(version => (
              <label key={version.id} className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name="gwpVersion"
                  value={version.id}
                  checked={formData.gwpVersion === version.id}
                  onChange={(e) => handleInputChange('gwpVersion', null, e.target.value)}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500"
                />
                <div>
                  <span className="font-medium text-gray-900">{version.name}</span>
                  {version.default && (
                    <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded">{t('workflow.calculation.factors.gwpVersion.default')}</span>
                  )}
                </div>
              </label>
            ))}
            {errors.gwpVersion && (
              <p className="text-sm text-red-600 flex items-center">
                <AlertCircle className="w-4 h-4 mr-1" />
                {t('workflow.calculation.factors.gwpVersion.required')}
              </p>
            )}
          </div>
        </div>

        {/* Factor Sources */}
        <div className="bg-gray-50 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800">{t('workflow.calculation.factors.factorSources.title')}</h3>
            <div className="flex items-center space-x-2">
              <Search className="w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder={t('workflow.calculation.factors.factorSources.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {Object.keys(formData.factorSources).map(category => (
              <div key={category} className="bg-white p-4 rounded border">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-gray-800 capitalize">{category}</h4>
                  <button
                    type="button"
                    onClick={() => togglePin(category)}
                    className={`p-1 rounded ${
                      formData.factorSources[category].pinned
                        ? 'text-blue-600 bg-blue-50'
                        : 'text-gray-400 hover:text-gray-600'
                    }`}
                    title={formData.factorSources[category].pinned ? t('workflow.calculation.factors.factorSources.unpinVersion') : t('workflow.calculation.factors.factorSources.pinVersion')}
                  >
                    <Pin className={`w-4 h-4 ${formData.factorSources[category].pinned ? 'fill-current' : ''}`} />
                  </button>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                    <select
                      value={formData.factorSources[category].source}
                      onChange={(e) => handleFactorSourceChange(category, 'source', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors[`${category}_source`] ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">{t('workflow.calculation.factors.factorSources.selectSource')}</option>
                      {filteredSources.map(source => (
                        <option key={source.id} value={source.id}>
                          {source.name} - {source.description}
                        </option>
                      ))}
                    </select>
                    {errors[`${category}_source`] && (
                      <p className="text-sm text-red-600">{errors[`${category}_source`]}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('workflow.calculation.factors.factorSources.version')}</label>
                    <input
                      type="text"
                      value={formData.factorSources[category].version}
                      onChange={(e) => handleFactorSourceChange(category, 'version', e.target.value)}
                      placeholder={t('workflow.calculation.factors.factorSources.versionPlaceholder')}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors[`${category}_version`] ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors[`${category}_version`] && (
                      <p className="text-sm text-red-600">{t('workflow.calculation.factors.factorSources.versionRequired')}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Regionalization */}
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('workflow.calculation.factors.regionalization.title')}</h3>
          <p className="text-gray-600 mb-4">{t('workflow.calculation.factors.regionalization.description')}</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('workflow.calculation.factors.regionalization.country')}</label>
              <select
                value={formData.regionalization.country}
                onChange={(e) => handleInputChange('regionalization', 'country', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.country ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">{t('workflow.calculation.factors.regionalization.selectCountry')}</option>
                {countries.map(country => (
                  <option key={country.code} value={country.code}>
                    {country.name}
                  </option>
                ))}
              </select>
              {errors.country && (
                <p className="text-sm text-red-600">{t('workflow.calculation.factors.regionalization.countryRequired')}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('workflow.calculation.factors.regionalization.subRegion')}</label>
              <input
                type="text"
                value={formData.regionalization.subRegion}
                onChange={(e) => handleInputChange('regionalization', 'subRegion', e.target.value)}
                placeholder={t('workflow.calculation.factors.regionalization.subRegionPlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Market-based Options */}
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('workflow.calculation.factors.marketBased.title')}</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('workflow.calculation.factors.marketBased.method')}</label>
              <div className="space-y-2">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="marketMethod"
                    value="supplier"
                    checked={formData.marketBased.method === 'supplier'}
                    onChange={(e) => handleMarketBasedChange('method', e.target.value)}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500"
                  />
                  <span className="font-medium text-gray-900">{t('workflow.calculation.factors.marketBased.supplierSpecific')}</span>
                </label>
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="marketMethod"
                    value="residual"
                    checked={formData.marketBased.method === 'residual'}
                    onChange={(e) => handleMarketBasedChange('method', e.target.value)}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500"
                  />
                  <span className="font-medium text-gray-900">{t('workflow.calculation.factors.marketBased.residualMix')}</span>
                </label>
              </div>
            </div>

            {formData.marketBased.method === 'supplier' && (
              <div className="bg-white p-4 rounded border">
                <h4 className="font-medium text-gray-800 mb-3">{t('workflow.calculation.factors.marketBased.supplierEvidence.title')}</h4>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('workflow.calculation.factors.marketBased.supplierEvidence.emissionFactor')}</label>
                    <input
                      type="text"
                      value={formData.marketBased.supplierSpecific.factor}
                      onChange={(e) => handleMarketBasedObjectChange('supplierSpecific', {
                        factor: e.target.value
                      })}
                      placeholder={t('workflow.calculation.factors.marketBased.supplierEvidence.emissionFactorPlaceholder')}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.supplierFactor ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.supplierFactor && (
                      <p className="text-sm text-red-600">{t('workflow.calculation.factors.marketBased.supplierEvidence.factorRequired')}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('workflow.calculation.factors.marketBased.supplierEvidence.contractLink')}</label>
                      <input
                        type="text"
                        value={formData.marketBased.supplierSpecific.evidence.contract}
                        onChange={(e) => handleSupplierEvidenceChange('contract', e.target.value)}
                        placeholder={t('workflow.calculation.factors.marketBased.supplierEvidence.contractPlaceholder')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('workflow.calculation.factors.marketBased.supplierEvidence.guaranteesOfOrigin')}</label>
                      <input
                        type="text"
                        value={formData.marketBased.supplierSpecific.evidence.guarantees}
                        onChange={(e) => handleSupplierEvidenceChange('guarantees', e.target.value)}
                        placeholder={t('workflow.calculation.factors.marketBased.supplierEvidence.guaranteesPlaceholder')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('workflow.calculation.factors.marketBased.supplierEvidence.ppaLink')}</label>
                      <input
                        type="text"
                        value={formData.marketBased.supplierSpecific.evidence.ppa}
                        onChange={(e) => handleSupplierEvidenceChange('ppa', e.target.value)}
                        placeholder={t('workflow.calculation.factors.marketBased.supplierEvidence.ppaPlaceholder')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  {errors.supplierEvidence && (
                    <p className="text-sm text-red-600">{t('workflow.calculation.factors.marketBased.supplierEvidence.evidenceRequired')}</p>
                  )}
                </div>
              </div>
            )}

            {formData.marketBased.method === 'residual' && (
              <div className="bg-white p-4 rounded border">
                <h4 className="font-medium text-gray-800 mb-3">{t('workflow.calculation.factors.marketBased.residualMixEvidence.title')}</h4>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('workflow.calculation.factors.marketBased.residualMixEvidence.emissionFactor')}</label>
                  <input
                    type="text"
                    value={formData.marketBased.residualMix.factor}
                    onChange={(e) => handleMarketBasedObjectChange('residualMix', {
                      factor: e.target.value
                    })}
                    placeholder={t('workflow.calculation.factors.marketBased.residualMixEvidence.emissionFactorPlaceholder')}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.residualFactor ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.residualFactor && (
                    <p className="text-sm text-red-600">{t('workflow.calculation.factors.marketBased.residualMixEvidence.factorRequired')}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Pinning */}
        <div className="bg-gray-50 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-800">{t('workflow.calculation.factors.pinning.title')}</h3>
              <p className="text-gray-600">{t('workflow.calculation.factors.pinning.description')}</p>
            </div>
            <button
              type="button"
              onClick={toggleGlobalPin}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
                formData.pinned
                  ? 'bg-blue-100 text-blue-700 border border-blue-300'
                  : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
              }`}
            >
              <Pin className={`w-4 h-4 ${formData.pinned ? 'fill-current' : ''}`} />
              <span>{formData.pinned ? t('workflow.calculation.factors.pinning.pinned') : t('workflow.calculation.factors.pinning.pinAll')}</span>
            </button>
          </div>

          {formData.pinned && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 text-yellow-600 mr-2" />
                <p className="text-sm text-yellow-800">
                  {t('workflow.calculation.factors.pinning.warning')}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Navigation handled by WorkflowTabView */}
      </form>
    </div>
  );
};

export default Factors;
